<template>
    <div>
        <loader :loader="isLoading" v-if="isLoading" />
        <div class="animated fadeIn" v-else>
            <b-row class="m-0 p-0">
                <b-col cols="12" md="4" xl="3" class="pt-3 pl-3 pr-3 pb-0 p-md-2">
                    <b-card class="p-2 mb-0">
                        <b-row class="px-2">
                            <user-data :user="user" :currentUser="currentUser" />
                        </b-row>
                        <b-row class="mt-4" v-if="canViewOkr">
                            <section-slot :section="$t('okr.myOkrs')" :hasBorder="false" sectionIcon="fas fa-sitemap">
                                <OKR-summary :user="user" />
                            </section-slot>
                        </b-row>
                        <div v-if="canViewValues">
                            <spontaneous-evaluation :evaluated="user" />
                        </div>
                        <b-row class="mt-4" v-if="canViewFeedbacks">
                            <section-slot sectionIcon="fas fa-comment-dots" section="Feedbacks" :hasBorder="false">
                                <talk-summary />
                            </section-slot>
                        </b-row>
                        <b-row class="mt-4" v-if="user.stages.all.length">
                            <section-slot :section="$t('stages.stages')" sectionIcon="far fa-calendar">
                                <stages :user="user" :currentUser="currentUser" @updated="onUpdatedStages" />
                            </section-slot>
                        </b-row>
                        <div v-if="canViewValues && isPeriodsReady">
                            <quadrants :user="user" :periods="periodIds" />
                        </div>
                        <b-row class="mt-4" v-if="canViewPDI">
                            <section-slot :section="$t('pdis.pdi')" :hasBorder="false" sectionIcon="fas fa-mountain">
                                <pdi-widget :userId="userID" />
                            </section-slot>
                        </b-row>
                        <b-row class="mt-4" v-if="canViewTarget">
                            <section-slot
                                :section="$t('targets.targets')"
                                :hasBorder="false"
                                sectionIcon="fas fa-crosshairs"
                            >
                                <targets-summary :user="user" />
                            </section-slot>
                        </b-row>
                    </b-card>
                </b-col>
                <b-col cols="12" md="8" xl="9" class="p-3 p-md-2">
                    <div v-if="isCurrentUserItself">
                        <b-card v-show="userHasPendencies" class="animated fadeIn card" header-tag="header">
                            <template #header>
                                <CardHeader
                                    :state="togglePendencies"
                                    module="pendencies"
                                    @onToggleCollapse="(event) => setTogglePendenciesCollapse(event)"
                                />
                            </template>
                            <b-collapse v-model="togglePendencies" id="pendenciesCollapse">
                                <pendencies
                                    :user="user"
                                    :haseNPSModule="isCurrentUserHasModule('enps')"
                                    :hasMoodPulseModule="isCurrentUserHasModule('quiz')"
                                    :hasValuesModule="isCurrentUserHasModule('value')"
                                    @hasPendency="isTherePendencies($event)"
                                />
                            </b-collapse>
                        </b-card>
                    </div>
                    <b-card>
                        <b-row>
                            <b-col sm="2" xl="1">
                                <div class="view-type">
                                    <button
                                        :class="[isListViewMode ? 'btn-primary' : 'btn-outline-primary', 'btn btn-sm']"
                                        v-b-tooltip.hover
                                        :title="$t('profile.viewModeList')"
                                        @click="setListViewMode(true)"
                                    >
                                        <i class="fas fa-bars"></i>
                                    </button>
                                    <button
                                        :class="[!isListViewMode ? 'btn-primary' : 'btn-outline-primary', 'btn btn-sm']"
                                        v-b-tooltip.hover
                                        :title="$t('profile.viewModeTabs')"
                                        @click="setListViewMode(false)"
                                    >
                                        <i class="fas fa-bars fa-rotate-90"></i>
                                    </button>
                                </div>
                            </b-col>
                            <b-col sm="7" xl="6">
                                <periods
                                    :hideSelectedOptions="false"
                                    :numberOfSelectedOptionsToDisplay="2"
                                    :closeOnSelect="false"
                                    :state="null"
                                    :value.sync="selectedPeriods"
                                    :placeholder="$t('form.placeholder.selectPeriods')"
                                    @ready="isPeriodsReady = $event"
                                    applyScopeNotArchived
                                />
                            </b-col>
                            <b-col>
                                <widget-create-object
                                    v-if="canViewWidgetCreateObject"
                                    :user="user"
                                    @objectiveCreated="onUpdateOKR"
                                    @pdiCreated="getPdisFromAPI"
                                    class="float-right"
                                    style="margin-top: 0.8rem"
                                />
                            </b-col>
                        </b-row>
                        <b-row class="mt-4">
                            <b-col>
                                <template v-if="isListViewMode">
                                    <one-page
                                        :user="user"
                                        :periods="periodIds"
                                        :permissions="{
                                            canViewPDI,
                                            canViewValues,
                                            canViewCareerObjectives,
                                            canViewMiniCurriculum,
                                            canViewComments,
                                            canViewTarget,
                                            canViewSubordinates,
                                            canViewOKR,
                                            canViewInitiatives,
                                        }"
                                        :isToShowSomeTabs="isToShowSomeTabs"
                                    />
                                </template>
                                <template v-else>
                                    <tabs
                                        :user="user"
                                        :periods="periodIds"
                                        :permissions="{
                                            canViewPDI,
                                            canViewValues,
                                            canViewCareerObjectives,
                                            canViewMiniCurriculum,
                                            canViewComments,
                                            canViewTarget,
                                            canViewSubordinates,
                                            canViewOKR,
                                            canViewInitiatives,
                                        }"
                                        :isToShowSomeTabs="isToShowSomeTabs"
                                    />
                                </template>
                            </b-col>
                        </b-row>
                    </b-card>
                </b-col>
            </b-row>
            <router-view
                :evaluated="user"
                @modalClosed="onModalClosed"
                @onSubmittedCheckin="onUpdateOKR"
                @onUpdatedKeyResult="onUpdateOKR"
                @onUpdatedObjective="onUpdateOKR"
            />
        </div>
    </div>
</template>

<script>
import Loader from '@/views/components/loader'
import { OnePage, Tabs } from './view-mode'
import {
    UserData,
    Talk,
    Stages,
    PdiWidget,
    Quadrants,
    SpontaneousEvaluation,
    SectionSlot,
    TargetsSummary,
    TalkSummary,
    Pendencies,
    OKRSummary,
    WidgetCreateObject,
} from './components'
import { Periods } from '@/views/components/fields'
import { getParam } from '@/lib/url'
import { getUserFromAPI, getTargets, getSubordinates } from './api'
import { userPermissionsMixin } from '@/mixins'
import { includes } from 'lodash'
import notification from '@/lib/notification'
import CardHeader from './view-mode/components'

notification.init()

export default {
    name: 'Profile',
    mixins: [userPermissionsMixin],
    components: {
        Loader,
        OnePage,
        Tabs,
        UserData,
        Talk,
        Stages,
        PdiWidget,
        Quadrants,
        SpontaneousEvaluation,
        Periods,
        SectionSlot,
        TargetsSummary,
        TalkSummary,
        Pendencies,
        OKRSummary,
        CardHeader,
        WidgetCreateObject,
    },
    data() {
        return {
            userID: getParam('id', this),
            isListViewMode: false,
            selectedPeriods: [],
            isPeriodsReady: false,
            isUserReady: false,
            userHasPendencies: false,
            togglePendencies: true,
            user: {},
            isToShowSomeTabs: {
                hiringGoals: false,
                focalPoint: false,
                subordinates: false,
            },
            pendingEvaluations: null,
        }
    },
    created() {
        this.setCurrentUserID()
    },
    mounted() {
        let listViewModeData = JSON.parse(localStorage.getItem('PROFILE_LIST_VIEW_MODE'))

        if (listViewModeData) {
            listViewModeData.some((item) => {
                if (item.userID === this.currentUser.id) {
                    this.isListViewMode = item.listViewMode
                }
            })
        }

        let pendenciesViewModeValue = this.getPendenciesViewModeValue()

        if (pendenciesViewModeValue != null) {
            this.togglePendencies = pendenciesViewModeValue
        }

        this.getUserFromAPI(this.userID)
        this.isThereSubordinates()
    },
    computed: {
        periodIds() {
            return this.selectedPeriods.map((item) => item.value)
        },
        isLoading() {
            //It is necessary to deny the expression so that when the requests are ready the loader is removed.
            return (!this.isPeriodsReady && !this.isUserReady) || !this.isUserReady
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        periods() {
            return this.$store.state.filters.periods
        },
        //Permissions
        canViewPDI() {
            const userHasPDI = this.isUserHasModule('pdi')
            const currentUserHasPDI = this.isCurrentUserHasModule('pdi')

            if (this.isCurrentUserItself) {
                return currentUserHasPDI
            }

            return (
                userHasPDI &&
                currentUserHasPDI &&
                (this.isCurrentUserAdmin ||
                    this.isCurrentUserGod ||
                    this.isCurrentUserManager ||
                    this.isCurrentUserValuesMaster ||
                    this.isCommitteeEvaluator ||
                    this.isCurrentUserLocalAdministrator)
            )
        },
        canViewOKR() {
            return this.isUserHasModule('okr') && this.isCurrentUserHasModule('okr')
        },
        canViewInitiatives() {
            return this.isUserHasModule('daily')
        },
        canViewCareerObjectives() {
            const userHasCareerObjective = this.isUserHasModule('career_objective')
            const currentUserHasCareerObjective = this.isCurrentUserHasModule('career_objective')

            return (
                userHasCareerObjective &&
                currentUserHasCareerObjective &&
                (this.isCurrentUserAdmin ||
                    this.isCurrentUserItself ||
                    this.isCurrentUserGod ||
                    this.isCurrentUserValuesMaster ||
                    this.isCurrentUserManager ||
                    this.isCommitteeEvaluator ||
                    this.isCurrentUserLocalAdministrator)
            )
        },
        canViewMiniCurriculum() {
            const userHasMiniCurriculum = this.isUserHasModule('mini_curriculum')
            const currentUserHasMiniCurriculum = this.isCurrentUserHasModule('mini_curriculum')
            return (
                userHasMiniCurriculum &&
                currentUserHasMiniCurriculum &&
                (this.isCurrentUserAdmin ||
                    this.isCurrentUserItself ||
                    this.isCurrentUserGod ||
                    this.isCurrentUserValuesMaster ||
                    this.isCurrentUserManager ||
                    this.isCommitteeEvaluator ||
                    this.isCurrentUserLocalAdministrator)
            )
        },
        canViewComments() {
            return (
                this.isntCurrentUserItself &&
                (this.isCurrentUserAdmin ||
                    this.isCurrentUserGod ||
                    this.isCurrentUserManager ||
                    this.isCommitteeEvaluator ||
                    this.isCurrentUserValuesMaster ||
                    this.isCurrentUserLocalAdministrator)
            )
        },
        canViewValues() {
            const userHasValues = this.isUserHasModule('value')
            const currentUserHasValues = this.isCurrentUserHasModule('value')
            if (this.isCurrentUserItself) {
                return currentUserHasValues
            }
            return (
                currentUserHasValues &&
                userHasValues &&
                (this.isCurrentUserAdmin ||
                    this.isCurrentUserGod ||
                    this.isCurrentUserManager ||
                    this.isCurrentUserValuesMaster ||
                    this.isCommitteeEvaluator ||
                    this.isCurrentUserLocalAdministrator)
            )
        },
        canViewFeedbacks() {
            let canViewFeedbacks = false
            const userHasTalk = this.isUserHasModule('talk')
            const currentUserHasTalk = this.isCurrentUserHasModule('talk')
            if (this.isCurrentUserItself) {
                canViewFeedbacks = currentUserHasTalk
            } else {
                canViewFeedbacks = currentUserHasTalk && userHasTalk
            }
            return canViewFeedbacks
        },
        canViewTarget() {
            let canViewTarget = false
            const userHasTarget = this.isUserHasModule('goal')
            const currentUserHasTarget = this.isCurrentUserHasModule('goal')
            if (this.isCurrentUserItself) {
                canViewTarget = currentUserHasTarget
            } else {
                canViewTarget =
                    userHasTarget &&
                    currentUserHasTarget &&
                    (this.isCurrentUserAdmin ||
                        this.isCurrentUserManager ||
                        this.isCurrentUserGod ||
                        this.isCommitteeEvaluator ||
                        this.isCurrentUserLocalAdministrator ||
                        this.isCurrentUserTargetMaster)
            }
            return canViewTarget
        },
        canViewOkr() {
            let canViewOkr = false
            const userHasOkr = this.isUserHasModule('okr')
            const currentUserHasOkr = this.isCurrentUserHasModule('okr')
            if (this.isCurrentUserItself) {
                canViewOkr = currentUserHasOkr
            } else {
                canViewOkr =
                    userHasOkr &&
                    currentUserHasOkr &&
                    (this.isCurrentUserAdmin ||
                        this.isCurrentUserManager ||
                        this.isCurrentUserGod ||
                        this.isCommitteeEvaluator ||
                        this.isCurrentUserLocalAdministrator)
            }
            return canViewOkr
        },
        //Permissions helpers
        isCurrentUserAdmin() {
            return this.currentUser.role.name == 'administrator'
        },
        isCurrentUserGod() {
            return this.currentUser.role.name == 'god'
        },
        isCurrentUserLocalAdministrator() {
            return this.currentUser.role.name === 'local_administrator'
        },
        isCurrentUserManager() {
            return this.currentUser.id == this.user.manager.id
        },
        isCurrentUserItself() {
            return this.currentUser.id == this.user.id
        },
        isntCurrentUserItself() {
            return this.currentUser.id != this.user.id
        },
        isCurrentUserValuesMaster() {
            return this.currentUser.is_values_master
        },
        isCurrentUserTargetMaster() {
            return this.currentUser.is_target_master
        },
        isCommitteeEvaluator() {
            //I'm in userPermissionsMixin
            return this.isCommitteeMember(this.currentUser.evaluator_committees, this.user.rated_committees)
        },
        canViewSubordinates() {
            return (
                this.isCurrentUserManager ||
                this.isCurrentUserAdmin ||
                this.isCurrentUserGod ||
                this.isCurrentUserItself
            )
        },
        canViewWidgetCreateObject() {
            return this.canViewOKR || this.canViewPDI
        },
    },
    methods: {
        isCurrentUserHasModule(module) {
            return includes(this.currentUser.has_modules, `has_${module}`)
        },
        isUserHasModule(module) {
            return includes(this.user.has_modules, `has_${module}`)
        },
        onModalClosed(modal) {
            if (modal == 'pdi-check-in') {
                this.$store.dispatch('reloadPDIs')
            } else {
                this.$store.dispatch('reloadTargets')
            }
        },
        setListViewMode(bool) {
            let newlistViewMode = {
                userID: this.currentUser.id,
                listViewMode: bool,
            }

            let listViewMode = []

            let currentListViewMode = JSON.parse(localStorage.getItem('PROFILE_LIST_VIEW_MODE'))

            if (currentListViewMode) {
                currentListViewMode.some((item) => {
                    if (item.userID != this.currentUser.id) {
                        listViewMode.push(item)
                    }
                })
            }

            listViewMode.push(newlistViewMode)

            localStorage.setItem('PROFILE_LIST_VIEW_MODE', JSON.stringify(listViewMode))
            this.isListViewMode = bool
        },
        getPendenciesViewModeValue() {
            return JSON.parse(localStorage.getItem('PROFILE_PENDENCIES_VIEW_MODE'))
        },
        setTogglePendenciesCollapse(bool) {
            localStorage.setItem('PROFILE_PENDENCIES_VIEW_MODE', bool)
            this.togglePendencies = bool
        },
        onUpdatedStages() {
            this.getUserFromAPI(this.userID)
        },
        getUserFromAPI(userID) {
            this.isUserReady = false
            getUserFromAPI(userID)
                .then((resp) => {
                    this.user = resp.data.data
                    this.$store.dispatch('profile/setUser', resp.data.data)
                })
                .catch((err) => {
                    this.$router.push('/v2/')
                    console.log(err)
                })
                .finally(() => {
                    this.isUserReady = true
                })
        },
        isThereHiringGoals() {
            getTargets(this.userID, this.periods, 'library-goals').then((goals) => {
                this.isToShowSomeTabs.hiringGoals = !!goals.length
            })
        },
        isThereFocalPoints() {
            getTargets(this.userID, this.periods, 'focal-points').then((goals) => {
                this.isToShowSomeTabs.focalPoint = !!goals.length
            })
        },
        isThereSubordinates() {
            getSubordinates(this.userID).then((subordinates) => {
                this.isToShowSomeTabs.subordinates = !!subordinates.data.length
            })
        },
        theUserBeingSeenHasTargetModule() {
            if (this.canViewTarget) {
                this.isThereHiringGoals()
                this.isThereFocalPoints()
            }
        },
        isTherePendencies(data) {
            this.userHasPendencies = data
        },
        setCurrentUserID() {
            const userID = this.$route.params.id
            this.$store.dispatch('profile/setCurrentUserID', userID)
        },
        getKRsFromAPI() {
            this.$store.dispatch('getKRsFromAPI')
        },
        getOKRsFromAPI() {
            this.$store.dispatch('getOKRsFromAPI')
        },
        getPdisFromAPI() {
            this.$store.dispatch('reloadPDIs')
        },
        onUpdateOKR() {
            getUserFromAPI(this.userID).then((resp) => {
                this.user = resp.data.data
                this.$store.dispatch('profile/setUser', resp.data.data)
            })
            this.getKRsFromAPI()
            this.getOKRsFromAPI()
        },
    },
    watch: {
        $route(to, from) {
            if (to.params.id !== from.params.id) {
                this.userID = to.params.id
                this.getUserFromAPI(to.params.id)
                this.theUserBeingSeenHasTargetModule()
                this.isThereSubordinates()
                this.setCurrentUserID()
            }
        },
        selectedPeriods() {
            this.$store.dispatch('applyPeriodsFilter', {
                periods: this.selectedPeriods,
                userId: this.user.id,
            })
            this.$store.dispatch('daily/getInitiativesFromAPI')
            this.$store.dispatch('getOKRsFromAPI')
        },
        periods() {
            this.theUserBeingSeenHasTargetModule()
        },
    },
}
</script>
<style lang="sass" scoped>
.card
    border-radius: 10px;
    border: none;
    margin-bottom: 1rem !important;
.view-type
    display: flex;
    width: 100%;
    margin-top: 0.8rem;
    gap: 8px;

.card-header
    padding: 0
    border-radius: 10px 10px 0 0;

@media screen and (max-width: 1700px)
    ::v-deep .obj.group-items.w-100.alignment-fix
        width:  50% !important;

    ::v-deep .row.kr .group-items.w-100.alignment-fix
        width: 48% !important;

    ::v-deep td.due_date
        padding-right: 1rem !important;

@media only screen and (max-width: 1600px)and (min-width: 1400px)
    ::v-deep .row.kr .status-counter
        margin-left: .6rem;
@media only screen and (max-width: 1800px)and (min-width: 1600px)
    ::v-deep .row.kr .status-counter
        margin-left: .1rem;
@media only screen and (max-width: 1400px)and (min-width: 800px)
    ::v-deep .row.kr .status-counter
        margin-left: .4rem;
</style>
